import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Phone, Mail, CheckCircle, MapPin, Monitor } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useSEO, SEO_PRESETS } from '@/hooks/useSEO';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { de } from 'date-fns/locale';
import { format, addDays, isBefore, isWeekend, startOfDay } from 'date-fns';

interface TimeSlot {
  start: string;
  end: string;
  type: 'remote' | 'onsite';
}

interface BlockedSlot {
  block_date: string;
  start_time: string;
  end_time: string;
}

interface ExistingAppointment {
  appointment_date: string;
  start_time: string;
  end_time: string;
}

const Appointments = () => {
  // SEO für Terminbuchung
  useSEO(SEO_PRESETS.appointments);
  
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [blockedSlots, setBlockedSlots] = useState<BlockedSlot[]>([]);
  const [existingAppointments, setExistingAppointments] = useState<ExistingAppointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const { toast } = useToast();

  // Generate time slots based on rules
  // Remote: 8:00 - 17:00
  // Onsite: 17:30+
  const generateTimeSlots = (date: Date): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Remote slots (8:00 - 17:00, 1 hour each)
    for (let hour = 8; hour < 17; hour++) {
      const startTime = `${hour.toString().padStart(2, '0')}:00`;
      const endTime = `${(hour + 1).toString().padStart(2, '0')}:00`;
      
      // Check if slot is blocked
      const isBlocked = blockedSlots.some(blocked => 
        blocked.block_date === dateStr &&
        blocked.start_time <= startTime &&
        blocked.end_time > startTime
      );
      
      // Check if slot already has an appointment
      const hasAppointment = existingAppointments.some(apt =>
        apt.appointment_date === dateStr &&
        apt.start_time <= startTime &&
        apt.end_time > startTime
      );
      
      if (!isBlocked && !hasAppointment) {
        slots.push({ start: startTime, end: endTime, type: 'remote' });
      }
    }
    
    // Onsite slots (17:30 - 20:00, 1.5 hours each)
    const onsiteStartTimes = ['17:30', '19:00'];
    for (const startTime of onsiteStartTimes) {
      const [hours, mins] = startTime.split(':').map(Number);
      const endHour = hours + 1;
      const endMins = mins + 30;
      const endTime = `${endHour.toString().padStart(2, '0')}:${(endMins % 60).toString().padStart(2, '0')}`;
      
      const isBlocked = blockedSlots.some(blocked => 
        blocked.block_date === dateStr &&
        blocked.start_time <= startTime &&
        blocked.end_time > startTime
      );
      
      const hasAppointment = existingAppointments.some(apt =>
        apt.appointment_date === dateStr &&
        apt.start_time <= startTime &&
        apt.end_time > startTime
      );
      
      if (!isBlocked && !hasAppointment) {
        slots.push({ start: startTime, end: endTime, type: 'onsite' });
      }
    }
    
    return slots;
  };

  // Fetch blocked slots and existing appointments when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailability(selectedDate);
    }
  }, [selectedDate]);

  const fetchAvailability = async (date: Date) => {
    setLoading(true);
    const dateStr = format(date, 'yyyy-MM-dd');
    
    try {
      // First, try to sync Google Calendar blocked times for this week
      try {
        const startDate = format(date, 'yyyy-MM-dd');
        const endDate = format(addDays(date, 7), 'yyyy-MM-dd');
        
        await supabase.functions.invoke('google-calendar-sync', {
          body: { 
            action: 'fetch_blocked_times',
            startDate: `${startDate}T00:00:00Z`,
            endDate: `${endDate}T23:59:59Z`
          },
        });
      } catch (syncError) {
        // Silently ignore if Google Calendar is not connected
        console.log('Google Calendar sync skipped:', syncError);
      }

      // Fetch blocked slots for this date (using public view that excludes sensitive 'reason' field)
      const { data: blocked, error: blockedError } = await supabase
        .from('blocked_slots_public')
        .select('block_date, start_time, end_time')
        .eq('block_date', dateStr);
      
      if (blockedError) throw blockedError;
      setBlockedSlots(
        (blocked || []).map((b) => ({
          ...b,
          start_time: (b.start_time || '').slice(0, 5),
          end_time: (b.end_time || '').slice(0, 5),
        }))
      );
      
      // Fetch existing appointments for this date (pending or confirmed only)
      const { data: appointments, error: aptError } = await supabase
        .from('appointments')
        .select('appointment_date, start_time, end_time')
        .eq('appointment_date', dateStr)
        .in('status', ['pending', 'confirmed']);
      
      if (aptError) throw aptError;
      setExistingAppointments(
        (appointments || []).map((a) => ({
          ...a,
          start_time: (a.start_time || '').slice(0, 5),
          end_time: (a.end_time || '').slice(0, 5),
        }))
      );
      const slots = generateTimeSlots(date);
      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error fetching availability:', error);
      toast({
        title: 'Fehler',
        description: 'Verfügbarkeit konnte nicht geladen werden.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Regenerate slots when blocked/appointments change
  useEffect(() => {
    if (selectedDate) {
      const slots = generateTimeSlots(selectedDate);
      setAvailableSlots(slots);
    }
  }, [blockedSlots, existingAppointments]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot: TimeSlot) => {
    setSelectedSlot(slot);
    setStep(2);
  };

  const BOOKING_COOLDOWN_MS = 300000; // 5 minutes between bookings

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate || !selectedSlot) return;

    // Rate limiting: Check for recent booking
    const lastBookingTime = localStorage.getItem('lastAppointmentBookingTime');
    const now = Date.now();
    if (lastBookingTime && now - parseInt(lastBookingTime) < BOOKING_COOLDOWN_MS) {
      const remainingSeconds = Math.ceil((BOOKING_COOLDOWN_MS - (now - parseInt(lastBookingTime))) / 1000);
      toast({
        title: 'Bitte warten',
        description: `Sie können in ${Math.ceil(remainingSeconds / 60)} Minuten erneut einen Termin buchen.`,
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const appointmentData = {
        customer_name: customerData.name,
        customer_email: customerData.email,
        customer_phone: customerData.phone || null,
        customer_message: customerData.message || null,
        appointment_type: selectedSlot.type as 'remote' | 'onsite',
        appointment_date: format(selectedDate, 'yyyy-MM-dd'),
        start_time: selectedSlot.start,
        end_time: selectedSlot.end,
        status: 'pending' as const,
      };

      const { error } = await supabase
        .from('appointments')
        .insert([appointmentData]);

      if (error) throw error;

      // Set last booking time for rate limiting
      localStorage.setItem('lastAppointmentBookingTime', now.toString());

      // Send notification email to admin
      try {
        await supabase.functions.invoke('send-appointment-notification', {
          body: {
            type: 'new_booking',
            ...appointmentData,
          },
        });
      } catch (emailError) {
        console.error('Failed to send notification email:', emailError);
      }

      setStep(3);
      toast({
        title: 'Terminanfrage gesendet!',
        description: 'Wir werden Ihren Termin so schnell wie möglich bestätigen.',
      });
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      toast({
        title: 'Fehler',
        description: 'Termin konnte nicht gebucht werden. Bitte versuchen Sie es erneut.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isDateDisabled = (date: Date) => {
    const today = startOfDay(new Date());
    return isBefore(date, addDays(today, 1)) || isWeekend(date);
  };

  if (step === 3) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-teal-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="shadow-xl">
            <CardContent className="p-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-10 w-10 text-green-500" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">
                  Terminanfrage gesendet!
                </h1>
                <p className="text-gray-600 mb-8">
                  Vielen Dank für Ihre Terminanfrage. Wir werden Ihren Termin prüfen und Ihnen eine Bestätigung per E-Mail senden.
                </p>
              </div>

              <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-xl p-6 mb-8">
                <h3 className="font-semibold text-gray-900 mb-4">Ihre Termindetails</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <User className="h-5 w-5 text-cyan-600 mr-3" />
                    <span>{customerData.name}</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-cyan-600 mr-3" />
                    <span>{customerData.email}</span>
                  </div>
                  <div className="flex items-center">
                    {selectedSlot?.type === 'remote' ? (
                      <Monitor className="h-5 w-5 text-cyan-600 mr-3" />
                    ) : (
                      <MapPin className="h-5 w-5 text-cyan-600 mr-3" />
                    )}
                    <span>{selectedSlot?.type === 'remote' ? 'Remote-Termin' : 'Vor-Ort-Termin'}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-cyan-600 mr-3" />
                    <span>{selectedDate && format(selectedDate, 'EEEE, d. MMMM yyyy', { locale: de })}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-cyan-600 mr-3" />
                    <span>{selectedSlot?.start} - {selectedSlot?.end} Uhr</span>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <Button
                  onClick={() => window.location.href = '/'}
                  className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white"
                >
                  Zur Startseite
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-teal-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Termin buchen
          </h1>
          <p className="text-xl text-gray-600 mb-4">
            Wählen Sie Ihren gewünschten Termin
          </p>
          <p className="text-base text-gray-500 max-w-2xl mx-auto bg-cyan-50 border border-cyan-200 rounded-xl px-6 py-4">
            Im kostenlosen Erstgespräch finden wir heraus, welches Betreuungspaket am besten zu Ihnen passt – oder ob ein Einmal-Support reicht.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              step >= 1 ? 'bg-cyan-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              1
            </div>
            <div className={`h-1 w-16 ${step >= 2 ? 'bg-cyan-600' : 'bg-gray-200'}`}></div>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              step >= 2 ? 'bg-cyan-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              2
            </div>
            <div className={`h-1 w-16 ${step >= 3 ? 'bg-cyan-600' : 'bg-gray-200'}`}></div>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              step >= 3 ? 'bg-cyan-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              3
            </div>
          </div>
        </div>

        <Card className="shadow-xl">
          <CardContent className="p-8">
            {step === 1 && (
              <>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Datum und Uhrzeit wählen
                </h2>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Calendar */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Datum auswählen
                    </label>
                    <div className="flex justify-center">
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={handleDateSelect}
                        disabled={isDateDisabled}
                        locale={de}
                        className="rounded-md border pointer-events-auto"
                        classNames={{}}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-2 text-center">
                      Montag - Freitag, ab morgen buchbar
                    </p>
                  </div>

                  {/* Time Slots */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Verfügbare Zeiten {selectedDate && `für ${format(selectedDate, 'd. MMMM', { locale: de })}`}
                    </label>

                    {!selectedDate ? (
                      <div className="text-center py-12 text-gray-500">
                        <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>Bitte wählen Sie zuerst ein Datum</p>
                      </div>
                    ) : loading ? (
                      <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div className="space-y-4">
                        {/* Remote Slots */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Monitor className="h-4 w-4 text-blue-600" />
                            <span className="text-sm font-medium text-gray-700">Remote-Termine (8:00 - 17:00)</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            {availableSlots
                              .filter(slot => slot.type === 'remote')
                              .map((slot, index) => (
                                <button
                                  key={index}
                                  onClick={() => handleSlotSelect(slot)}
                                  className="px-3 py-2 border-2 border-blue-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 text-sm"
                                >
                                  {slot.start}
                                </button>
                              ))}
                          </div>
                        </div>

                        {/* Onsite Slots */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <MapPin className="h-4 w-4 text-teal-600" />
                            <span className="text-sm font-medium text-gray-700">Vor-Ort-Termine (ab 17:30)</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {availableSlots
                              .filter(slot => slot.type === 'onsite')
                              .map((slot, index) => (
                                <button
                                  key={index}
                                  onClick={() => handleSlotSelect(slot)}
                                  className="px-3 py-2 border-2 border-teal-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 text-sm"
                                >
                                  {slot.start} - {slot.end}
                                </button>
                              ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>Keine verfügbaren Termine für diesen Tag</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {step === 2 && (
              <form onSubmit={handleSubmit}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Ihre Kontaktdaten
                  </h2>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                  >
                    Zurück
                  </Button>
                </div>

                {/* Selected Slot Summary */}
                <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      {selectedSlot?.type === 'remote' ? (
                        <Monitor className="h-5 w-5 text-blue-600" />
                      ) : (
                        <MapPin className="h-5 w-5 text-teal-600" />
                      )}
                      <span className="font-medium">
                        {selectedSlot?.type === 'remote' ? 'Remote-Termin' : 'Vor-Ort-Termin'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-cyan-600" />
                      <span>{selectedDate && format(selectedDate, 'd. MMMM yyyy', { locale: de })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-cyan-600" />
                      <span>{selectedSlot?.start} - {selectedSlot?.end} Uhr</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <Input
                      type="text"
                      required
                      value={customerData.name}
                      onChange={(e) => setCustomerData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ihr vollständiger Name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      E-Mail *
                    </label>
                    <Input
                      type="email"
                      required
                      value={customerData.email}
                      onChange={(e) => setCustomerData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="ihre@email.de"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon *
                    </label>
                    <Input
                      type="tel"
                      required
                      value={customerData.phone}
                      onChange={(e) => setCustomerData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="Ihre Telefonnummer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Wie können wir Ihnen helfen?
                    </label>
                    <Textarea
                      value={customerData.message}
                      onChange={(e) => setCustomerData(prev => ({ ...prev, message: e.target.value }))}
                      rows={4}
                      placeholder="Beschreiben Sie kurz Ihr Anliegen..."
                    />
                  </div>
                </div>

                <div className="mt-8">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-cyan-600 to-teal-600 text-white py-3"
                  >
                    {submitting ? 'Wird gesendet...' : 'Terminanfrage senden'}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <div className="mt-8 bg-white rounded-xl p-6 shadow-lg">
          <h3 className="font-semibold text-gray-900 mb-4">Hinweise zur Terminbuchung</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div className="flex items-start gap-2">
              <Monitor className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <strong>Remote-Termine (8:00 - 17:00)</strong>
                <p>Unterstützung per Fernwartung, ideal für schnelle Problemlösung</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="h-5 w-5 text-teal-600 mt-0.5" />
              <div>
                <strong>Vor-Ort-Termine (ab 17:30)</strong>
                <p>Persönlicher Service bei Ihnen zu Hause oder im Büro</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Appointments;
